var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};

// ../shared/src/base64FromBytes.ts
function base64FromBytes(arr) {
  if (globalThis.Buffer) {
    return globalThis.Buffer.from(arr).toString("base64");
  } else {
    const bin = [];
    arr.forEach((byte) => {
      bin.push(String.fromCharCode(byte));
    });
    return globalThis.btoa(bin.join(""));
  }
}

// ../shared/src/isBackend.ts
var isBrowser = typeof window !== "undefined" && typeof window.document !== "undefined";
var isWebWorker = typeof self === "object" && self.constructor && self.constructor.name === "DedicatedWorkerGlobalScope";
var isBackend = !isBrowser && !isWebWorker;

// ../shared/src/isFrontend.ts
var isFrontend = !isBackend;

// src/consts.ts
var HUB_URL = typeof process !== "undefined" && false ? "https://hub-ci.huggingface.co" : "https://huggingface.co";

// src/error.ts
async function createApiError(response, opts) {
  var _a, _b;
  const error = new HubApiError(response.url, response.status, (_a = response.headers.get("X-Request-Id")) != null ? _a : opts == null ? void 0 : opts.requestId);
  error.message = `Api error with status ${error.statusCode}.${(opts == null ? void 0 : opts.message) ? ` ${opts.message}.` : ""} Request ID: ${error.requestId}, url: ${error.url}`;
  if ((_b = response.headers.get("Content-Type")) == null ? void 0 : _b.startsWith("application/json")) {
    const json = await response.json();
    error.message = json.error || json.message || error.message;
    error.data = json;
  } else {
    error.data = { message: await response.text() };
  }
  throw error;
}
var HubApiError = class extends Error {
  constructor(url, statusCode, requestId, message) {
    super(message);
    __publicField(this, "statusCode");
    __publicField(this, "url");
    __publicField(this, "requestId");
    __publicField(this, "data");
    this.statusCode = statusCode;
    this.requestId = requestId;
    this.url = url;
  }
};
var InvalidApiResponseFormatError = class extends Error {
};

// src/utils/checkCredentials.ts
function checkCredentials(credentials) {
  if (!credentials || credentials.accessToken === void 0 || credentials.accessToken === null) {
    return;
  }
  if (!credentials.accessToken.startsWith("hf_")) {
    throw new TypeError("Your access token must start with 'hf_'");
  }
}

// src/utils/range.ts
function range(n, b) {
  return b ? Array(b - n).fill(0).map((_, i) => n + i) : Array(n).fill(0).map((_, i) => i);
}

// src/utils/chunk.ts
function chunk(arr, chunkSize) {
  if (isNaN(chunkSize) || chunkSize < 1) {
    throw new RangeError("Invalid chunk size: " + chunkSize);
  }
  if (!arr.length) {
    return [];
  }
  if (arr.length <= chunkSize) {
    return [arr];
  }
  return range(Math.ceil(arr.length / chunkSize)).map((i) => {
    return arr.slice(i * chunkSize, (i + 1) * chunkSize);
  });
}

// src/utils/promisesQueue.ts
async function promisesQueue(factories, concurrency) {
  const results = [];
  const executing = /* @__PURE__ */ new Set();
  let index = 0;
  for (const factory of factories) {
    const closureIndex = index++;
    const e = factory().then((r) => {
      results[closureIndex] = r;
      executing.delete(e);
    });
    executing.add(e);
    if (executing.size >= concurrency) {
      await Promise.race(executing);
    }
  }
  await Promise.all(executing);
  return results;
}

// src/utils/promisesQueueStreaming.ts
async function promisesQueueStreaming(factories, concurrency) {
  const executing = [];
  for await (const factory of factories) {
    const e = factory().then(() => {
      executing.splice(executing.indexOf(e), 1);
    });
    executing.push(e);
    if (executing.length >= concurrency) {
      await Promise.race(executing);
    }
  }
  await Promise.all(executing);
}

// src/utils/hexFromBytes.ts
function hexFromBytes(arr) {
  if (globalThis.Buffer) {
    return globalThis.Buffer.from(arr).toString("hex");
  } else {
    const bin = [];
    arr.forEach((byte) => {
      bin.push(byte.toString(16).padStart(2, "0"));
    });
    return bin.join("");
  }
}

// src/utils/sha256.ts
async function sha256(buffer) {
  var _a;
  if (buffer.size < 1e7 && ((_a = globalThis.crypto) == null ? void 0 : _a.subtle)) {
    return hexFromBytes(
      new Uint8Array(
        await globalThis.crypto.subtle.digest("SHA-256", buffer instanceof Blob ? await buffer.arrayBuffer() : buffer)
      )
    );
  }
  if (isFrontend) {
    if (!wasmModule) {
      wasmModule = await import("hash-wasm");
    }
    const sha2562 = await wasmModule.createSHA256();
    sha2562.init();
    const reader = buffer.stream().getReader();
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      sha2562.update(value);
    }
    return sha2562.digest("hex");
  }
  if (!cryptoModule) {
    cryptoModule = await import("./sha256-node-TNZ2WHTI.mjs");
  }
  return cryptoModule.sha256Node(buffer);
}
var cryptoModule;
var wasmModule;

// src/utils/toRepoId.ts
function toRepoId(repo) {
  if (typeof repo !== "string") {
    return repo;
  }
  if (repo.startsWith("model/") || repo.startsWith("models/")) {
    throw new TypeError(
      "A repo designation for a model should not start with 'models/', directly specify the model namespace / name"
    );
  }
  if (repo.startsWith("space/")) {
    throw new TypeError("Spaces should start with 'spaces/', plural, not 'space/'");
  }
  if (repo.startsWith("dataset/")) {
    throw new TypeError("Datasets should start with 'dataset/', plural, not 'dataset/'");
  }
  const slashes = repo.split("/").length - 1;
  if (repo.startsWith("spaces/")) {
    if (slashes !== 2) {
      throw new TypeError("Space Id must include namespace and name of the space");
    }
    return {
      type: "space",
      name: repo.slice("spaces/".length)
    };
  }
  if (repo.startsWith("datasets/")) {
    if (slashes > 2) {
      throw new TypeError("Too many slashes in repo designation: " + repo);
    }
    return {
      type: "dataset",
      name: repo.slice("datasets/".length)
    };
  }
  if (slashes > 1) {
    throw new TypeError("Too many slashes in repo designation: " + repo);
  }
  return {
    type: "model",
    name: repo
  };
}

// src/utils/WebBlob.ts
var WebBlob = class extends Blob {
  constructor(url, start, end, contentType, full, customFetch) {
    super([]);
    __publicField(this, "url");
    __publicField(this, "start");
    __publicField(this, "end");
    __publicField(this, "contentType");
    __publicField(this, "full");
    __publicField(this, "fetch");
    this.url = url;
    this.start = start;
    this.end = end;
    this.contentType = contentType;
    this.full = full;
    this.fetch = customFetch;
  }
  static async create(url, opts) {
    var _a, _b;
    const customFetch = (_a = opts == null ? void 0 : opts.fetch) != null ? _a : fetch;
    const response = await customFetch(url, { method: "HEAD" });
    const size = Number(response.headers.get("content-length"));
    const contentType = response.headers.get("content-type") || "";
    const supportRange = response.headers.get("accept-ranges") === "bytes";
    if (!supportRange || size < ((_b = opts == null ? void 0 : opts.cacheBelow) != null ? _b : 1e6)) {
      return await (await customFetch(url)).blob();
    }
    return new WebBlob(url, 0, size, contentType, true, customFetch);
  }
  get size() {
    return this.end - this.start;
  }
  get type() {
    return this.contentType;
  }
  slice(start = 0, end = this.size) {
    if (start < 0 || end < 0) {
      new TypeError("Unsupported negative start/end on FileBlob.slice");
    }
    const slice = new WebBlob(
      this.url,
      this.start + start,
      Math.min(this.start + end, this.end),
      this.contentType,
      start === 0 && end === this.size ? this.full : false,
      this.fetch
    );
    return slice;
  }
  async arrayBuffer() {
    const result = await this.fetchRange();
    return result.arrayBuffer();
  }
  async text() {
    const result = await this.fetchRange();
    return result.text();
  }
  stream() {
    const stream = new TransformStream();
    this.fetchRange().then((response) => {
      var _a;
      return (_a = response.body) == null ? void 0 : _a.pipeThrough(stream);
    }).catch((error) => stream.writable.abort(error.message));
    return stream.readable;
  }
  fetchRange() {
    const fetch2 = this.fetch;
    if (this.full) {
      return fetch2(this.url);
    }
    return fetch2(this.url, {
      headers: {
        Range: `bytes=${this.start}-${this.end - 1}`
      }
    });
  }
};

// src/utils/createBlob.ts
async function createBlob(url, opts) {
  if (url.protocol === "http:" || url.protocol === "https:") {
    return WebBlob.create(url, { fetch: opts == null ? void 0 : opts.fetch });
  }
  if (isFrontend) {
    throw new TypeError(`Unsupported URL protocol "${url.protocol}"`);
  }
  if (url.protocol === "file:") {
    const { FileBlob } = await import("./FileBlob-7MRLQ6TG.mjs");
    return FileBlob.create(url);
  }
  throw new TypeError(`Unsupported URL protocol "${url.protocol}"`);
}

// src/lib/commit.ts
var CONCURRENT_SHAS = 5;
var CONCURRENT_LFS_UPLOADS = 5;
var MULTIPART_PARALLEL_UPLOAD = 5;
function isFileOperation(op) {
  const ret = op.operation === "addOrUpdate";
  if (ret && !(op.content instanceof Blob)) {
    throw new TypeError("Precondition failed: op.content should be a Blob");
  }
  return ret;
}
async function* commitIter(params) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j;
  checkCredentials(params.credentials);
  const repoId = toRepoId(params.repo);
  yield "preuploading";
  const lfsShas = /* @__PURE__ */ new Map();
  const allOperations = await Promise.all(
    params.operations.map(async (operation) => {
      if (operation.operation !== "addOrUpdate") {
        return operation;
      }
      if (!(operation.content instanceof URL)) {
        return { ...operation, content: operation.content };
      }
      const lazyBlob = await createBlob(operation.content, { fetch: params.fetch });
      return {
        ...operation,
        content: lazyBlob
      };
    })
  );
  const gitAttributes = (_a = allOperations.filter(isFileOperation).find((op) => op.path === ".gitattributes")) == null ? void 0 : _a.content;
  for (const operations of chunk(allOperations.filter(isFileOperation), 100)) {
    const payload = {
      gitAttributes: gitAttributes && await gitAttributes.text(),
      files: await Promise.all(
        operations.map(async (operation) => ({
          path: operation.path,
          size: operation.content.size,
          sample: base64FromBytes(new Uint8Array(await operation.content.slice(0, 512).arrayBuffer()))
        }))
      )
    };
    const res2 = await ((_b = params.fetch) != null ? _b : fetch)(
      `${(_c = params.hubUrl) != null ? _c : HUB_URL}/api/${repoId.type}s/${repoId.name}/preupload/${encodeURIComponent(
        (_d = params.branch) != null ? _d : "main"
      )}` + (params.isPullRequest ? "?create_pr=1" : ""),
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${params.credentials.accessToken}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      }
    );
    if (!res2.ok) {
      throw await createApiError(res2);
    }
    const json2 = await res2.json();
    for (const file of json2.files) {
      if (file.uploadMode === "lfs") {
        lfsShas.set(file.path, null);
      }
    }
  }
  yield "uploading to LFS";
  for (const operations of chunk(
    allOperations.filter(isFileOperation).filter((op) => lfsShas.has(op.path)),
    100
  )) {
    yield `hashing ${operations.length} files`;
    const shas = await promisesQueue(
      operations.map((op) => async () => {
        const sha = await sha256(op.content);
        lfsShas.set(op.path, sha);
        return sha;
      }),
      CONCURRENT_SHAS
    );
    const payload = {
      operation: "upload",
      // multipart is a custom protocol for HF
      transfers: ["basic", "multipart"],
      hash_algo: "sha_256",
      ref: {
        name: (_e = params.branch) != null ? _e : "main"
      },
      objects: operations.map((op, i) => ({
        oid: shas[i],
        size: op.content.size
      }))
    };
    const res2 = await ((_f = params.fetch) != null ? _f : fetch)(
      `${(_g = params.hubUrl) != null ? _g : HUB_URL}/${repoId.type === "model" ? "" : repoId.type + "s/"}${repoId.name}.git/info/lfs/objects/batch`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${params.credentials.accessToken}`,
          Accept: "application/vnd.git-lfs+json",
          "Content-Type": "application/vnd.git-lfs+json"
        },
        body: JSON.stringify(payload)
      }
    );
    if (!res2.ok) {
      throw await createApiError(res2);
    }
    const json2 = await res2.json();
    const batchRequestId = res2.headers.get("X-Request-Id") || void 0;
    const shaToOperation = new Map(operations.map((op, i) => [shas[i], op]));
    await promisesQueueStreaming(
      json2.objects.map((obj) => async () => {
        var _a2, _b2, _c2;
        const op = shaToOperation.get(obj.oid);
        if (!op) {
          throw new InvalidApiResponseFormatError("Unrequested object ID in response");
        }
        if (obj.error) {
          const errorMessage = `Error while doing LFS batch call for ${operations[shas.indexOf(obj.oid)].path}: ${obj.error.message}${batchRequestId ? ` - Request ID: ${batchRequestId}` : ""}`;
          throw new HubApiError(res2.url, obj.error.code, batchRequestId, errorMessage);
        }
        if (!((_a2 = obj.actions) == null ? void 0 : _a2.upload)) {
          return;
        }
        const content = op.content;
        const header = obj.actions.upload.header;
        if (header == null ? void 0 : header.chunk_size) {
          const chunkSize = parseInt(header.chunk_size);
          const completionUrl = obj.actions.upload.href;
          const parts = Object.keys(header).filter((key) => /^[0-9]+$/.test(key));
          if (parts.length !== Math.ceil(content.size / chunkSize)) {
            throw new Error("Invalid server response to upload large LFS file, wrong number of parts");
          }
          const completeReq = {
            oid: obj.oid,
            parts: parts.map((part) => ({
              partNumber: +part,
              etag: ""
            }))
          };
          await promisesQueueStreaming(
            parts.map((part) => async () => {
              var _a3;
              const index = parseInt(part) - 1;
              const slice = content.slice(index * chunkSize, (index + 1) * chunkSize);
              const res4 = await ((_a3 = params.fetch) != null ? _a3 : fetch)(header[part], {
                method: "PUT",
                /** Unfortunately, browsers don't support our inherited version of Blob in fetch calls */
                body: slice instanceof WebBlob && isFrontend ? await slice.arrayBuffer() : slice
              });
              if (!res4.ok) {
                throw await createApiError(res4, {
                  requestId: batchRequestId,
                  message: `Error while uploading part ${part} of ${operations[shas.indexOf(obj.oid)].path} to LFS storage`
                });
              }
              const eTag = res4.headers.get("ETag");
              if (!eTag) {
                throw new Error("Cannot get ETag of part during multipart upload");
              }
              completeReq.parts[Number(part) - 1].etag = eTag;
            }),
            MULTIPART_PARALLEL_UPLOAD
          );
          const res3 = await ((_b2 = params.fetch) != null ? _b2 : fetch)(completionUrl, {
            method: "POST",
            body: JSON.stringify(completeReq),
            headers: {
              Accept: "application/vnd.git-lfs+json",
              "Content-Type": "application/vnd.git-lfs+json"
            }
          });
          if (!res3.ok) {
            throw await createApiError(res3, {
              requestId: batchRequestId,
              message: `Error completing multipart upload of ${operations[shas.indexOf(obj.oid)].path} to LFS storage`
            });
          }
        } else {
          const res3 = await ((_c2 = params.fetch) != null ? _c2 : fetch)(obj.actions.upload.href, {
            method: "PUT",
            headers: {
              ...batchRequestId ? { "X-Request-Id": batchRequestId } : void 0
            },
            /** Unfortunately, browsers don't support our inherited version of Blob in fetch calls */
            body: content instanceof WebBlob && isFrontend ? await content.arrayBuffer() : content
          });
          if (!res3.ok) {
            throw await createApiError(res3, {
              requestId: batchRequestId,
              message: `Error while uploading ${operations[shas.indexOf(obj.oid)].path} to LFS storage`
            });
          }
        }
      }),
      CONCURRENT_LFS_UPLOADS
    );
  }
  yield "committing";
  const res = await ((_h = params.fetch) != null ? _h : fetch)(
    `${(_i = params.hubUrl) != null ? _i : HUB_URL}/api/${repoId.type}s/${repoId.name}/commit/${encodeURIComponent(
      (_j = params.branch) != null ? _j : "main"
    )}` + (params.isPullRequest ? "?create_pr=1" : ""),
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${params.credentials.accessToken}`,
        "Content-Type": "application/x-ndjson"
      },
      body: [
        {
          key: "header",
          value: {
            summary: params.title,
            description: params.description,
            parentCommit: params.parentCommit
          }
        },
        ...await Promise.all(
          allOperations.map((operation) => {
            if (isFileOperation(operation)) {
              const sha = lfsShas.get(operation.path);
              if (sha) {
                return {
                  key: "lfsFile",
                  value: {
                    path: operation.path,
                    algo: "sha256",
                    size: operation.content.size,
                    oid: sha
                  }
                };
              }
            }
            return convertOperationToNdJson(operation);
          })
        )
      ].map((x) => JSON.stringify(x)).join("\n")
    }
  );
  if (!res.ok) {
    throw await createApiError(res);
  }
  const json = await res.json();
  return {
    pullRequestUrl: json.pullRequestUrl,
    commit: {
      oid: json.commitOid,
      url: json.commitUrl
    },
    hookOutput: json.hookOutput
  };
}
async function commit(params) {
  const iterator = commitIter(params);
  let res = await iterator.next();
  while (!res.done) {
    res = await iterator.next();
  }
  return res.value;
}
async function convertOperationToNdJson(operation) {
  switch (operation.operation) {
    case "addOrUpdate": {
      return {
        key: "file",
        value: {
          content: base64FromBytes(new Uint8Array(await operation.content.arrayBuffer())),
          path: operation.path,
          encoding: "base64"
        }
      };
    }
    case "delete": {
      return {
        key: "deletedFile",
        value: {
          path: operation.path
        }
      };
    }
    default:
      throw new TypeError("Unknown operation: " + operation.operation);
  }
}

// src/lib/create-repo.ts
async function createRepo(params) {
  var _a, _b;
  checkCredentials(params.credentials);
  const repoId = toRepoId(params.repo);
  const [namespace, repoName] = repoId.name.split("/");
  if (!namespace || !repoName) {
    throw new TypeError(
      `"${repoId.name}" is not a fully qualified repo name. It should be of the form "{namespace}/{repoName}".`
    );
  }
  const res = await ((_a = params.fetch) != null ? _a : fetch)(`${(_b = params.hubUrl) != null ? _b : HUB_URL}/api/repos/create`, {
    method: "POST",
    body: JSON.stringify({
      name: repoName,
      private: params.private,
      organization: namespace,
      license: params.license,
      ...repoId.type === "space" ? {
        type: "space",
        sdk: "static"
      } : {
        type: repoId.type
      },
      files: params.files ? await Promise.all(
        params.files.map(async (file) => ({
          encoding: "base64",
          path: file.path,
          content: base64FromBytes(
            new Uint8Array(file.content instanceof Blob ? await file.content.arrayBuffer() : file.content)
          )
        }))
      ) : void 0
    }),
    headers: {
      Authorization: `Bearer ${params.credentials.accessToken}`,
      "Content-Type": "application/json"
    }
  });
  if (!res.ok) {
    throw await createApiError(res);
  }
  const output = await res.json();
  return { repoUrl: output.url };
}

// src/lib/delete-file.ts
function deleteFile(params) {
  var _a;
  return commit({
    credentials: params.credentials,
    repo: params.repo,
    operations: [
      {
        operation: "delete",
        path: params.path
      }
    ],
    title: (_a = params.commitTitle) != null ? _a : `Delete ${params.path}`,
    description: params.commitDescription,
    hubUrl: params.hubUrl,
    branch: params.branch,
    isPullRequest: params.isPullRequest,
    parentCommit: params.parentCommit,
    fetch: params.fetch
  });
}

// src/lib/delete-files.ts
function deleteFiles(params) {
  var _a;
  return commit({
    credentials: params.credentials,
    repo: params.repo,
    operations: params.paths.map((path) => ({
      operation: "delete",
      path
    })),
    title: (_a = params.commitTitle) != null ? _a : `Deletes ${params.paths.length} files`,
    description: params.commitDescription,
    hubUrl: params.hubUrl,
    branch: params.branch,
    isPullRequest: params.isPullRequest,
    parentCommit: params.parentCommit,
    fetch: params.fetch
  });
}

// src/lib/delete-repo.ts
async function deleteRepo(params) {
  var _a, _b;
  checkCredentials(params.credentials);
  const repoId = toRepoId(params.repo);
  const [namespace, repoName] = repoId.name.split("/");
  const res = await ((_a = params.fetch) != null ? _a : fetch)(`${(_b = params.hubUrl) != null ? _b : HUB_URL}/api/repos/delete`, {
    method: "DELETE",
    body: JSON.stringify({
      name: repoName,
      organization: namespace,
      type: repoId.type
    }),
    headers: {
      Authorization: `Bearer ${params.credentials.accessToken}`,
      "Content-Type": "application/json"
    }
  });
  if (!res.ok) {
    throw await createApiError(res);
  }
}

// src/lib/download-file.ts
async function downloadFile(params) {
  var _a, _b, _c;
  checkCredentials(params.credentials);
  const repoId = toRepoId(params.repo);
  const url = `${(_a = params.hubUrl) != null ? _a : HUB_URL}/${repoId.type === "model" ? "" : `${repoId.type}s/`}${repoId.name}/${params.raw ? "raw" : "resolve"}/${encodeURIComponent((_b = params.revision) != null ? _b : "main")}/${params.path}`;
  const resp = await ((_c = params.fetch) != null ? _c : fetch)(url, {
    headers: {
      ...params.credentials ? {
        Authorization: `Bearer ${params.credentials.accessToken}`
      } : {},
      ...params.range ? {
        Range: `bytes=${params.range[0]}-${params.range[1]}`
      } : {}
    }
  });
  if (resp.status === 404 && resp.headers.get("X-Error-Code") === "EntryNotFound") {
    return null;
  } else if (!resp.ok) {
    throw await createApiError(resp);
  }
  return resp;
}

// src/lib/file-download-info.ts
async function fileDownloadInfo(params) {
  var _a, _b, _c;
  checkCredentials(params.credentials);
  const repoId = toRepoId(params.repo);
  const hubUrl = (_a = params.hubUrl) != null ? _a : HUB_URL;
  const url = `${hubUrl}/${repoId.type === "model" ? "" : `${repoId.type}s/`}${repoId.name}/${params.raw ? "raw" : "resolve"}/${encodeURIComponent((_b = params.revision) != null ? _b : "main")}/${params.path}` + (params.noContentDisposition ? "?noContentDisposition=1" : "");
  const resp = await ((_c = params.fetch) != null ? _c : fetch)(url, {
    method: "GET",
    headers: params.credentials ? {
      Authorization: `Bearer ${params.credentials.accessToken}`,
      Range: "bytes=0-0"
    } : {}
  });
  if (resp.status === 404 && resp.headers.get("X-Error-Code") === "EntryNotFound") {
    return null;
  }
  if (!resp.ok) {
    throw await createApiError(resp);
  }
  const etag = resp.headers.get("ETag");
  if (!etag) {
    throw new InvalidApiResponseFormatError("Expected ETag");
  }
  const sizeHeader = resp.headers.get("Content-Length");
  if (!sizeHeader) {
    throw new InvalidApiResponseFormatError("Expected size information");
  }
  const size = parseInt(sizeHeader);
  if (isNaN(size)) {
    throw new InvalidApiResponseFormatError("Invalid file size received");
  }
  return {
    etag,
    size,
    downloadLink: new URL(resp.url).hostname !== new URL(hubUrl).hostname ? resp.url : null
  };
}

// src/lib/file-exists.ts
async function fileExists(params) {
  const info = await fileDownloadInfo({ ...params, raw: true, fetch: params.fetch });
  return !!info;
}

// src/utils/parseLinkHeader.ts
function parseLinkHeader(header) {
  const regex = /<(https?:[/][/][^>]+)>;\s+rel="([^"]+)"/g;
  return Object.fromEntries([...header.matchAll(regex)].map(([, url, rel]) => [rel, url]));
}

// src/lib/list-datasets.ts
var EXPAND_KEYS = ["private", "downloads", "gated", "likes", "lastModified"];
async function* listDatasets(params) {
  var _a, _b;
  checkCredentials(params == null ? void 0 : params.credentials);
  const search = new URLSearchParams([
    ...Object.entries({
      limit: "500",
      ...((_a = params == null ? void 0 : params.search) == null ? void 0 : _a.owner) ? { author: params.search.owner } : void 0
    }),
    ...EXPAND_KEYS.map((val) => ["expand", val])
  ]).toString();
  let url = `${(params == null ? void 0 : params.hubUrl) || HUB_URL}/api/datasets` + (search ? "?" + search : "");
  while (url) {
    const res = await ((_b = params == null ? void 0 : params.fetch) != null ? _b : fetch)(url, {
      headers: {
        accept: "application/json",
        ...(params == null ? void 0 : params.credentials) ? { Authorization: `Bearer ${params.credentials.accessToken}` } : void 0
      }
    });
    if (!res.ok) {
      throw createApiError(res);
    }
    const items = await res.json();
    for (const item of items) {
      yield {
        id: item._id,
        name: item.id,
        private: item.private,
        downloads: item.downloads,
        likes: item.likes,
        gated: item.gated,
        updatedAt: new Date(item.lastModified)
      };
    }
    const linkHeader = res.headers.get("Link");
    url = linkHeader ? parseLinkHeader(linkHeader).next : void 0;
  }
}

// src/lib/list-files.ts
async function* listFiles(params) {
  var _a;
  checkCredentials(params.credentials);
  const repoId = toRepoId(params.repo);
  let url = `${params.hubUrl || HUB_URL}/api/${repoId.type}s/${repoId.name}/tree/${params.revision || "main"}${params.path ? "/" + params.path : ""}?recursive=${!!params.recursive}&expand=${!!params.expand}`;
  while (url) {
    const res = await ((_a = params.fetch) != null ? _a : fetch)(url, {
      headers: {
        accept: "application/json",
        ...params.credentials ? { Authorization: `Bearer ${params.credentials.accessToken}` } : void 0
      }
    });
    if (!res.ok) {
      throw createApiError(res);
    }
    const items = await res.json();
    for (const item of items) {
      yield item;
    }
    const linkHeader = res.headers.get("Link");
    url = linkHeader ? parseLinkHeader(linkHeader).next : void 0;
  }
}

// src/lib/list-models.ts
var EXPAND_KEYS2 = ["pipeline_tag", "private", "gated", "downloads", "likes"];
async function* listModels(params) {
  var _a, _b, _c;
  checkCredentials(params == null ? void 0 : params.credentials);
  const search = new URLSearchParams([
    ...Object.entries({
      limit: "500",
      ...((_a = params == null ? void 0 : params.search) == null ? void 0 : _a.owner) ? { author: params.search.owner } : void 0,
      ...((_b = params == null ? void 0 : params.search) == null ? void 0 : _b.task) ? { pipeline_tag: params.search.task } : void 0
    }),
    ...EXPAND_KEYS2.map((val) => ["expand", val])
  ]).toString();
  let url = `${(params == null ? void 0 : params.hubUrl) || HUB_URL}/api/models?${search}`;
  while (url) {
    const res = await ((_c = params == null ? void 0 : params.fetch) != null ? _c : fetch)(url, {
      headers: {
        accept: "application/json",
        ...(params == null ? void 0 : params.credentials) ? { Authorization: `Bearer ${params.credentials.accessToken}` } : void 0
      }
    });
    if (!res.ok) {
      throw createApiError(res);
    }
    const items = await res.json();
    for (const item of items) {
      yield {
        id: item._id,
        name: item.id,
        private: item.private,
        task: item.pipeline_tag,
        downloads: item.downloads,
        gated: item.gated,
        likes: item.likes,
        updatedAt: new Date(item.lastModified)
      };
    }
    const linkHeader = res.headers.get("Link");
    url = linkHeader ? parseLinkHeader(linkHeader).next : void 0;
  }
}

// src/utils/pick.ts
function pick(o, props) {
  return Object.assign(
    {},
    ...props.map((prop) => {
      if (o[prop] !== void 0) {
        return { [prop]: o[prop] };
      }
    })
  );
}

// src/lib/list-spaces.ts
var EXPAND_KEYS3 = ["sdk", "likes", "private", "lastModified"];
async function* listSpaces(params) {
  var _a, _b, _c;
  checkCredentials(params == null ? void 0 : params.credentials);
  const search = new URLSearchParams([
    ...Object.entries({ limit: "500", ...((_a = params == null ? void 0 : params.search) == null ? void 0 : _a.owner) ? { author: params.search.owner } : void 0 }),
    ...[...EXPAND_KEYS3, ...(_b = params == null ? void 0 : params.additionalFields) != null ? _b : []].map((val) => ["expand", val])
  ]).toString();
  let url = `${(params == null ? void 0 : params.hubUrl) || HUB_URL}/api/spaces?${search}`;
  while (url) {
    const res = await ((_c = params == null ? void 0 : params.fetch) != null ? _c : fetch)(url, {
      headers: {
        accept: "application/json",
        ...(params == null ? void 0 : params.credentials) ? { Authorization: `Bearer ${params.credentials.accessToken}` } : void 0
      }
    });
    if (!res.ok) {
      throw createApiError(res);
    }
    const items = await res.json();
    for (const item of items) {
      yield {
        ...(params == null ? void 0 : params.additionalFields) && pick(item, params.additionalFields),
        id: item._id,
        name: item.id,
        sdk: item.sdk,
        likes: item.likes,
        private: item.private,
        updatedAt: new Date(item.lastModified)
      };
    }
    const linkHeader = res.headers.get("Link");
    url = linkHeader ? parseLinkHeader(linkHeader).next : void 0;
  }
}

// src/utils/typedInclude.ts
function typedInclude(arr, v) {
  return arr.includes(v);
}

// src/utils/omit.ts
function omit(o, props) {
  const propsArr = Array.isArray(props) ? props : [props];
  const letsKeep = Object.keys(o).filter((prop) => !typedInclude(propsArr, prop));
  return pick(o, letsKeep);
}

// src/utils/typedEntries.ts
function typedEntries(obj) {
  return Object.entries(obj);
}

// src/lib/parse-safetensors-metadata.ts
var SINGLE_FILE = "model.safetensors";
var INDEX_FILE = "model.safetensors.index.json";
var PARALLEL_DOWNLOADS = 5;
async function parseSingleFile(path, params) {
  const firstResp = await downloadFile({
    ...params,
    path,
    range: [0, 7]
  });
  if (!firstResp) {
    throw new Error("Failed to download file: " + path);
  }
  const bufLengthOfHeaderLE = await firstResp.arrayBuffer();
  const lengthOfHeader = new DataView(bufLengthOfHeaderLE).getBigUint64(0, true);
  const secondResp = await downloadFile({ ...params, path, range: [8, 7 + Number(lengthOfHeader)] });
  if (!secondResp) {
    throw new Error("Failed to download file: " + path);
  }
  const header = await secondResp.json();
  return header;
}
async function parseShardedIndex(path, params) {
  const indexResp = await downloadFile({
    ...params,
    path
  });
  if (!indexResp) {
    throw new Error("Failed to download file: " + path);
  }
  const index = await indexResp.json();
  const filenames = [...new Set(Object.values(index.weight_map))];
  const shardedMap = Object.fromEntries(
    await promisesQueue(
      filenames.map(
        (filename) => async () => [filename, await parseSingleFile(filename, params)]
      ),
      PARALLEL_DOWNLOADS
    )
  );
  return { index, headers: shardedMap };
}
async function parseSafetensorsMetadata(params) {
  checkCredentials(params.credentials);
  const repoId = toRepoId(params.repo);
  if (repoId.type !== "model") {
    throw new TypeError("Only model repos should contain safetensors files.");
  }
  if (await fileExists({ ...params, path: SINGLE_FILE })) {
    const header = await parseSingleFile(SINGLE_FILE, params);
    return {
      sharded: false,
      header,
      ...params.computeParametersCount && {
        parameterCount: computeNumOfParamsByDtypeSingleFile(header)
      }
    };
  } else if (await fileExists({ ...params, path: INDEX_FILE })) {
    const { index, headers } = await parseShardedIndex(INDEX_FILE, params);
    return {
      sharded: true,
      index,
      headers,
      ...params.computeParametersCount && {
        parameterCount: computeNumOfParamsByDtypeSharded(headers)
      }
    };
  } else {
    throw new Error("model id does not seem to contain safetensors weights");
  }
}
function computeNumOfParamsByDtypeSingleFile(header) {
  var _a;
  const counter = {};
  const tensors = omit(header, "__metadata__");
  for (const [, v] of typedEntries(tensors)) {
    if (v.shape.length === 0) {
      continue;
    }
    counter[v.dtype] = ((_a = counter[v.dtype]) != null ? _a : 0) + v.shape.reduce((a, b) => a * b);
  }
  return counter;
}
function computeNumOfParamsByDtypeSharded(shardedMap) {
  var _a;
  const counter = {};
  for (const header of Object.values(shardedMap)) {
    for (const [k, v] of typedEntries(computeNumOfParamsByDtypeSingleFile(header))) {
      counter[k] = ((_a = counter[k]) != null ? _a : 0) + (v != null ? v : 0);
    }
  }
  return counter;
}

// src/lib/upload-file.ts
function uploadFile(params) {
  var _a, _b;
  const path = params.file instanceof URL ? (_a = params.file.pathname.split("/").at(-1)) != null ? _a : "file" : "path" in params.file ? params.file.path : params.file.name;
  return commit({
    credentials: params.credentials,
    repo: params.repo,
    operations: [
      {
        operation: "addOrUpdate",
        path,
        content: "content" in params.file ? params.file.content : params.file
      }
    ],
    title: (_b = params.commitTitle) != null ? _b : `Add ${path}`,
    description: params.commitDescription,
    hubUrl: params.hubUrl,
    branch: params.branch,
    isPullRequest: params.isPullRequest,
    parentCommit: params.parentCommit,
    fetch: params.fetch
  });
}

// src/lib/upload-files.ts
function uploadFiles(params) {
  var _a;
  return commit({
    credentials: params.credentials,
    repo: params.repo,
    operations: params.files.map((file) => {
      var _a2;
      return {
        operation: "addOrUpdate",
        path: file instanceof URL ? (_a2 = file.pathname.split("/").at(-1)) != null ? _a2 : "file" : "path" in file ? file.path : file.name,
        content: "content" in file ? file.content : file
      };
    }),
    title: (_a = params.commitTitle) != null ? _a : `Add ${params.files.length} files`,
    description: params.commitDescription,
    hubUrl: params.hubUrl,
    branch: params.branch,
    isPullRequest: params.isPullRequest,
    parentCommit: params.parentCommit,
    fetch: params.fetch
  });
}

// src/lib/who-am-i.ts
async function whoAmI(params) {
  var _a, _b, _c;
  checkCredentials(params.credentials);
  const res = await ((_a = params.fetch) != null ? _a : fetch)(`${(_b = params.hubUrl) != null ? _b : HUB_URL}/api/whoami-v2`, {
    headers: {
      Authorization: `Bearer ${params.credentials.accessToken}`
    }
  });
  if (!res.ok) {
    throw await createApiError(res);
  }
  const response = await res.json();
  if (typeof ((_c = response.auth.accessToken) == null ? void 0 : _c.expiration) === "string") {
    response.auth.accessToken.expiration = new Date(response.auth.accessToken.expiration);
  }
  return response;
}
export {
  HubApiError,
  InvalidApiResponseFormatError,
  commit,
  createRepo,
  deleteFile,
  deleteFiles,
  deleteRepo,
  downloadFile,
  fileDownloadInfo,
  fileExists,
  listDatasets,
  listFiles,
  listModels,
  listSpaces,
  parseSafetensorsMetadata,
  uploadFile,
  uploadFiles,
  whoAmI
};
